import { Suspense, useCallback, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Clock, LogOut, ServerCrash, ShieldAlert, WifiOff } from 'lucide-react';
import { useIsAuthenticated } from '@/stores/authStore';
import { can, type Action } from '@/services/auth/capabilities';
import { authService } from '@/services/auth/authService';
import { useUserProfile } from '@/services/user/hooks';
import { getHttpStatus, isApiError } from '@/api/errors';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/ui/button';
import { ErrorCapture } from '@/components/Errors/ErrorCapture';
import { ErrorTechnicalDetails } from '@/components/Errors/ErrorTechnicalDetails';
import { SupportReportButton } from '@/components/Errors/SupportReportButton';
import type { LucideIcon } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requires?: Action;
}

// Visually matches BootstrapSpinner in index.tsx so the bootstrap → React
// swap is invisible. `label` is for screen readers only.
const AuthLoading = ({ label }: { label: string }) => (
  <div
    className="flex h-screen items-center justify-center bg-[var(--background)]"
    role="status"
    aria-label={label}
  >
    <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-[var(--border)] border-t-[var(--foreground)]" />
  </div>
);

const AccessDenied = () => (
  <div className="flex h-screen items-center justify-center bg-background">
    <div className="max-w-md rounded-md border border-[var(--border)] bg-[var(--card)] p-8 text-center shadow-sm">
      <h2 className="text-lg font-semibold text-[var(--foreground)]">Access denied</h2>
      <p className="mt-2 text-sm text-[var(--muted-foreground)]">
        You don&apos;t have permission to view this page.
      </p>
    </div>
  </div>
);

interface ProfileErrorContent {
  icon: LucideIcon;
  title: string;
  message: string;
  showRetry: boolean;
  showSignOut: boolean;
}

// Maps a failed profile fetch to user-facing copy + affordances. 401/403 also
// trigger keycloak via handleApiAuthError, but we still render sensible content
// for the window before any redirect (and for embedded, where we never redirect).
function describeProfileError(error: unknown): ProfileErrorContent {
  // Not an HTTP failure at all — a component under this boundary threw during
  // render. Don't dress it up as a connectivity/profile problem.
  if (!isApiError(error) && !isAxiosError(error)) {
    return {
      icon: ServerCrash,
      title: 'Something went wrong',
      message: 'The page hit an unexpected error. Try again, and report it if it keeps happening.',
      showRetry: true,
      showSignOut: false,
    };
  }

  const status = getHttpStatus(error);

  // No HTTP response reached us at all — network down, CORS, DNS, server unreachable.
  if (status === undefined) {
    return {
      icon: WifiOff,
      title: "Can't reach the server",
      message: 'Check your internet connection and try again.',
      showRetry: true,
      showSignOut: false,
    };
  }

  switch (status) {
    case 401:
      return {
        icon: LogOut,
        title: 'Your session has expired',
        message: 'Sign out and sign back in to continue.',
        showRetry: false,
        showSignOut: true,
      };
    case 403:
      return {
        icon: ShieldAlert,
        title: 'Access denied',
        message:
          "Your account doesn't have access to this application. Contact your administrator if you think this is a mistake.",
        showRetry: false,
        showSignOut: true,
      };
    case 408:
    case 429:
    case 502:
    case 503:
    case 504:
      return {
        icon: Clock,
        title: 'Service temporarily unavailable',
        message: 'The server is busy or briefly unavailable. Please try again in a moment.',
        showRetry: true,
        showSignOut: false,
      };
    default:
      return {
        icon: ServerCrash,
        title: 'Something went wrong on our end',
        message: 'We hit an unexpected error loading your profile. Please try again.',
        showRetry: true,
        showSignOut: true,
      };
  }
}

const ProfileLoadError = ({
  error,
  componentStack,
  onRetry,
}: {
  error: unknown;
  componentStack?: string | null;
  onRetry: () => void;
}) => {
  const { icon: Icon, title, message, showRetry, showSignOut } = describeProfileError(error);

  return (
    <div className="flex h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-xl overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--card)] shadow-xl">
        <img
          src="/hazcheck-extract.svg"
          alt="Hazcheck Extract"
          className="px-4 h-16 w-auto dark:brightness-0 dark:invert"
        />
        <div className="px-10 py-8">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/5 ring-1 ring-primary/10">
              <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-[var(--foreground)]">{title}</h2>
              <p className="mt-1 text-sm leading-relaxed text-[var(--muted-foreground)]">
                {message}
              </p>
            </div>
          </div>
          <ErrorTechnicalDetails
            error={error}
            componentStack={componentStack}
            className="mt-6"
          />
          <div className="mt-8 flex justify-end gap-3">
            <SupportReportButton error={error} componentStack={componentStack} className="mr-auto" />
            {showSignOut && (
              <Button variant="outline" onClick={() => authService.logout()}>
                Sign out
              </Button>
            )}
            {showRetry && <Button onClick={onRetry}>Try again</Button>}
          </div>
        </div>
      </div>
    </div>
  );
};

// `onReset` runs before the boundary clears its error, so the child re-suspends against a fresh
// fetch rather than the stale failure.
const ProfileErrorBoundary = ({
  onRetry,
  children,
}: {
  onRetry: () => void;
  children: ReactNode;
}) => (
  <ErrorCapture
    onReset={onRetry}
    fallback={({ error, componentStack, reset }) => (
      <ProfileLoadError error={error} componentStack={componentStack} onRetry={reset} />
    )}
  >
    {children}
  </ErrorCapture>
);

const ProfileGate = ({ requires, children }: { requires?: Action; children: ReactNode }) => {
  const profile = useUserProfile();
  if (requires && !can(requires, profile)) return <AccessDenied />;
  return <>{children}</>;
};

const ProtectedRoute = ({ children, requires }: ProtectedRouteProps) => {
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();

  const retryProfile = useCallback(
    () => queryClient.resetQueries({ queryKey: queryKeys.user.profile() }),
    [queryClient]
  );

  // keycloak.init() is awaited before <App> mounts, so this flag is final.
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <ProfileErrorBoundary onRetry={retryProfile}>
      <Suspense fallback={<AuthLoading label="Loading profile..." />}>
        <ProfileGate requires={requires}>{children}</ProfileGate>
      </Suspense>
    </ProfileErrorBoundary>
  );
};

export default ProtectedRoute;
